// ===================================
// REGISTER PAGE - ENHANCED
// frontend/src/pages/Register.js
// Multi-step registration with email verification + Google OAuth
// ===================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { countries, defaultCountry } from '../data/countries';
import './Register.css';

// Registration steps
const STEPS = {
  CHOOSE_METHOD: 'choose_method',
  EMAIL_VERIFICATION: 'email_verification',
  COMPLETE_PROFILE: 'complete_profile'
};

const Register = () => {
  const navigate = useNavigate();
  // Use setAuth for OAuth/direct login (not login which makes API call)
  const { setAuth, isAuthenticated } = useAuth();
  
  // Current step
  const [currentStep, setCurrentStep] = useState(STEPS.CHOOSE_METHOD);
  
  // Email verification state
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [tempToken, setTempToken] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [codeExpiry, setCodeExpiry] = useState(0);
  
  // Profile form state
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    password: '',
    confirmPassword: '',
    country: defaultCountry,
    phoneNumber: ''
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleClientId, setGoogleClientId] = useState(null);
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null });
  
  // Refs for code inputs
  const codeInputRefs = useRef([]);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Fetch Google client ID
  useEffect(() => {
    const fetchGoogleConfig = async () => {
      try {
        const response = await api.get('/auth/google/config');
        if (response.data.clientId && response.data.enabled) {
          setGoogleClientId(response.data.clientId);
        }
      } catch (err) {
        console.log('Google OAuth not configured');
      }
    };
    fetchGoogleConfig();
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Code expiry countdown
  useEffect(() => {
    if (codeExpiry > 0) {
      const timer = setTimeout(() => setCodeExpiry(codeExpiry - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeExpiry]);

  // Check username availability (debounced)
  const checkUsername = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus({ checking: false, available: null });
      return;
    }

    setUsernameStatus({ checking: true, available: null });
    
    try {
      const response = await api.post('/auth/check-username', { username });
      setUsernameStatus({ 
        checking: false, 
        available: response.data.available,
        reason: response.data.reason
      });
    } catch (err) {
      setUsernameStatus({ checking: false, available: null });
    }
  }, []);

  // Debounced username check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username) {
        checkUsername(formData.username);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username, checkUsername]);

  // ===================================
  // STEP 1: EMAIL SUBMISSION
  // ===================================
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/send-verification', { email });
      
      if (response.data.exists) {
        // User exists - magic link sent
        setError('');
        setCurrentStep(STEPS.EMAIL_VERIFICATION);
        // Show magic link message
        alert('An account with this email already exists. We sent you a magic login link. Check your email!');
        navigate('/login');
      } else {
        // New user - verification code sent
        setCurrentStep(STEPS.EMAIL_VERIFICATION);
        setCodeExpiry(response.data.expiresIn || 600);
        setResendTimer(60); // 60 seconds before can resend
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  // ===================================
  // STEP 2: CODE VERIFICATION
  // ===================================
  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (value && index === 5 && newCode.every(digit => digit !== '')) {
      setTimeout(() => handleVerifyCode(newCode.join('')), 100);
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setVerificationCode(newCode);
      codeInputRefs.current[5]?.focus();
      setTimeout(() => handleVerifyCode(pastedData), 100);
    }
  };

  const handleVerifyCode = async (code = verificationCode.join('')) => {
    if (code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/verify-code', { email, code });
      
      if (response.data.verified) {
        setTempToken(response.data.tempToken);
        setCurrentStep(STEPS.COMPLETE_PROFILE);
      }
    } catch (err) {
      const errorData = err.response?.data;
      setError(errorData?.message || 'Invalid verification code');
      
      if (errorData?.expired || errorData?.tooManyAttempts) {
        // Reset code inputs
        setVerificationCode(['', '', '', '', '', '']);
        codeInputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/resend-code', { email });
      setVerificationCode(['', '', '', '', '', '']);
      setCodeExpiry(response.data.expiresIn || 600);
      setResendTimer(60);
      codeInputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // ===================================
  // STEP 3: COMPLETE PROFILE
  // ===================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleCountryChange = (e) => {
    const country = countries.find(c => c.code === e.target.value);
    if (country) {
      setFormData(prev => ({ ...prev, country }));
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return;
    }

    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }

    if (usernameStatus.available === false) {
      setError('Username is not available');
      return;
    }

    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/register', {
        tempToken,
        email,
        fullName: formData.fullName.trim(),
        username: formData.username.trim(),
        password: formData.password,
        countryCode: formData.country.code,
        countryName: formData.country.name,
        phoneCountryCode: formData.country.dialCode,
        phoneNumber: formData.phoneNumber.trim()
      });

      // Use setAuth (not login) - just store token and user, no API call
      setAuth(response.data.token, response.data.user);
      navigate('/app/dashboard');

    } catch (err) {
      const errorData = err.response?.data;
      setError(errorData?.message || 'Registration failed');
      
      if (errorData?.verificationExpired || errorData?.requiresVerification) {
        // Go back to verification step
        setCurrentStep(STEPS.CHOOSE_METHOD);
        setVerificationCode(['', '', '', '', '', '']);
      }
    } finally {
      setLoading(false);
    }
  };

  // ===================================
  // GOOGLE SIGN-IN
  // ===================================
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/google', {
        credential: credentialResponse.credential
      });

      // Use setAuth (not login) - just store token and user, no API call
      setAuth(response.data.token, response.data.user);
      
      // Check if profile needs completion
      if (response.data.user.needsProfileCompletion) {
        navigate('/app/settings?complete=true');
      } else {
        navigate('/app/dashboard');
      }

    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled');
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ===================================
  // RENDER
  // ===================================
  return (
    <div className="register-page">
      <div className="register-container">
        {/* Logo */}
        <div className="register-logo">
          <Link to="/">
            <span className="logo-icon">🎯</span>
            <span className="logo-text">CareerCoach.AI</span>
          </Link>
        </div>

        {/* ========================================
            HORIZONTAL STEP INDICATOR
            ======================================== */}
        {currentStep !== STEPS.CHOOSE_METHOD && (
          <div className="step-indicator">
            {/* Step 1 */}
            <div className={`step ${currentStep === STEPS.EMAIL_VERIFICATION ? 'active' : ''} ${currentStep === STEPS.COMPLETE_PROFILE ? 'completed' : ''}`}>
              <div className="step-number">1</div>
              <span className="step-label">Verify Email</span>
            </div>
            
            {/* Horizontal Connecting Line */}
            <div className={`step-line ${currentStep === STEPS.COMPLETE_PROFILE ? 'completed' : ''}`}></div>
            
            {/* Step 2 */}
            <div className={`step ${currentStep === STEPS.COMPLETE_PROFILE ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <span className="step-label">Create Profile</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="register-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* ===================================
            STEP 1: CHOOSE METHOD
            =================================== */}
        {currentStep === STEPS.CHOOSE_METHOD && (
          <div className="register-step choose-method">
            <h1>Create your account</h1>
            <p className="subtitle">Start practicing interviews with AI coaching</p>

            {/* Google Sign-In - PROMINENT */}
            {googleClientId && (
              <>
                <div className="google-recommended">Fastest way to sign up</div>
                <GoogleOAuthProvider clientId={googleClientId}>
                  <div className="google-signin-wrapper">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      useOneTap={false}
                      theme="outline"
                      size="large"
                      text="continue_with"
                      shape="rectangular"
                      width="400"
                      logo_alignment="center"
                    />
                  </div>
                </GoogleOAuthProvider>
              </>
            )}

            {googleClientId && <div className="divider"><span>or continue with email</span></div>}

            {/* Email Form */}
            <form onSubmit={handleEmailSubmit} className="email-form">
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Sending code...
                  </>
                ) : (
                  'Continue with Email →'
                )}
              </button>
            </form>

            <p className="login-link">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </div>
        )}

        {/* ===================================
            STEP 2: EMAIL VERIFICATION
            =================================== */}
        {currentStep === STEPS.EMAIL_VERIFICATION && (
          <div className="register-step email-verification">
            <div className="verification-icon">📬</div>
            <h1>Check your email</h1>
            <p className="subtitle">
              We sent a 6-digit code to<br />
              <strong>{email}</strong>
            </p>

            {/* Code Input */}
            <div className="code-input-wrapper" onPaste={handleCodePaste}>
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => codeInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  className="code-input"
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {/* Expiry Timer */}
            {codeExpiry > 0 && (
              <p className="code-expiry">
                Code expires in <span className="timer">{formatTime(codeExpiry)}</span>
              </p>
            )}

            {/* Verify Button */}
            <button
              className="btn btn-primary btn-block"
              onClick={() => handleVerifyCode()}
              disabled={loading || verificationCode.some(d => !d)}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </button>

            {/* Resend Link */}
            <div className="resend-section">
              {resendTimer > 0 ? (
                <p className="resend-timer">
                  Resend code in {resendTimer}s
                </p>
              ) : (
                <button
                  type="button"
                  className="resend-link"
                  onClick={handleResendCode}
                  disabled={loading}
                >
                  Didn't receive it? Resend code
                </button>
              )}
            </div>

            {/* Back Button */}
            <button
              type="button"
              className="back-link"
              onClick={() => {
                setCurrentStep(STEPS.CHOOSE_METHOD);
                setVerificationCode(['', '', '', '', '', '']);
                setError('');
              }}
            >
              ← Use a different email
            </button>
          </div>
        )}

        {/* ===================================
            STEP 3: COMPLETE PROFILE
            =================================== */}
        {currentStep === STEPS.COMPLETE_PROFILE && (
          <div className="register-step complete-profile">
            <h1>Complete your profile</h1>
            <p className="subtitle">Almost there! Fill in your details.</p>

            <form onSubmit={handleRegisterSubmit} className="profile-form">
              {/* Full Name */}
              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  required
                  disabled={loading}
                />
              </div>

              {/* Username */}
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <div className="input-with-status">
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="johnsmith"
                    required
                    disabled={loading}
                    className={
                      usernameStatus.available === true ? 'valid' :
                      usernameStatus.available === false ? 'invalid' : ''
                    }
                  />
                  {usernameStatus.checking && <span className="status-icon checking">⏳</span>}
                  {usernameStatus.available === true && <span className="status-icon available">✓</span>}
                  {usernameStatus.available === false && <span className="status-icon unavailable">✗</span>}
                </div>
                {usernameStatus.reason && (
                  <span className="field-hint error">{usernameStatus.reason}</span>
                )}
                {usernameStatus.available === false && !usernameStatus.reason && (
                  <span className="field-hint error">Username is already taken</span>
                )}
              </div>

              {/* Country */}
              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country.code}
                  onChange={handleCountryChange}
                  disabled={loading}
                >
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phone Number */}
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <div className="phone-input-wrapper">
                  <div className="dial-code">
                    <span className="flag">{formData.country.flag}</span>
                    <span className="code">{formData.country.dialCode}</span>
                  </div>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  disabled={loading}
                />
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Repeat your password"
                  required
                  disabled={loading}
                />
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <span className="field-hint error">Passwords don't match</span>
                )}
              </div>

              {/* Submit Button */}
              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <p className="terms-text">
              By creating an account, you agree to our{' '}
              <Link to="/terms">Terms of Service</Link> and{' '}
              <Link to="/privacy">Privacy Policy</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;